import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/layout';
import { paymentsAPI, coursesAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  CreditCard,
  TrendingUp,
  Receipt,
  Loader2,
  DollarSign,
  Percent,
  FileText,
  X,
  Save,
  Calendar,
  CheckCircle,
  Clock,
  AlertCircle,
  Search,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Download,
  BarChart2,
  Eye,
  EyeOff,
  FlaskConical,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';

const getStatusBadge = (status) => {
  if (status === 'paid')
    return (
      <Badge className="bg-emerald-100 text-emerald-700">
        <CheckCircle className="w-3 h-3 mr-1" />Paid
      </Badge>
    );
  if (status === 'pending')
    return (
      <Badge className="bg-yellow-100 text-yellow-700">
        <Clock className="w-3 h-3 mr-1" />Pending
      </Badge>
    );
  if (status === 'refunded')
    return (
      <Badge className="bg-red-100 text-red-700">
        <AlertCircle className="w-3 h-3 mr-1" />Refunded
      </Badge>
    );
  if (status === 'partially_refunded')
    return (
      <Badge className="bg-orange-100 text-orange-700">
        <AlertCircle className="w-3 h-3 mr-1" />Part. Refunded
      </Badge>
    );
  return <Badge className="bg-slate-100 text-slate-700">{status}</Badge>;
};

// Pricing Editor Modal
const PricingEditorModal = ({ course, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    price: course.price || 0,
    vat_rate: course.vat_rate || 20,
    subscription_enabled: course.subscription_enabled || false,
    subscription_price_monthly: course.subscription_price_monthly || 0,
    subscription_price_yearly: course.subscription_price_yearly || 0,
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await paymentsAPI.updateCoursePricing(course.course_id, formData);
      toast.success('Pricing updated successfully');
      onSave();
      onClose();
    } catch (err) {
      toast.error('Failed to update pricing');
    } finally {
      setSaving(false);
    }
  };

  const calculateWithVAT = (price) => {
    const vat = price * (formData.vat_rate / 100);
    return (price + vat).toFixed(2);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-100 sticky top-0 bg-white">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-[#0D1B2A]">Edit Course Pricing</h2>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-slate-500 mt-1">{course.title}</p>
        </div>

        <div className="p-6 space-y-6">
          {/* VAT Rate */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Percent className="w-4 h-4 text-slate-500" />
              VAT Rate (%)
            </Label>
            <Input
              type="number"
              step="0.1"
              value={formData.vat_rate}
              onChange={(e) => setFormData({ ...formData, vat_rate: parseFloat(e.target.value) || 0 })}
              className="w-full"
            />
            <p className="text-xs text-slate-500">UK standard rate is 20%</p>
          </div>

          {/* One-Time Price */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-slate-500" />
              One-Time Price (£)
            </Label>
            <Input
              type="number"
              step="0.01"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
              className="w-full"
            />
            {formData.price > 0 && (
              <p className="text-xs text-slate-500">
                With VAT: <span className="font-medium">£{calculateWithVAT(formData.price)}</span>
              </p>
            )}
          </div>

          {/* Subscription Toggle */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
            <div>
              <p className="font-medium text-[#0D1B2A]">Enable Subscriptions</p>
              <p className="text-xs text-slate-500">Allow monthly/yearly payment options</p>
            </div>
            <Switch
              checked={formData.subscription_enabled}
              onCheckedChange={(checked) => setFormData({ ...formData, subscription_enabled: checked })}
            />
          </div>

          {/* Subscription Prices */}
          {formData.subscription_enabled && (
            <div className="space-y-4 p-4 border border-slate-200 rounded-xl">
              <p className="font-medium text-[#0D1B2A]">Subscription Pricing</p>

              <div className="space-y-2">
                <Label>Monthly Price (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.subscription_price_monthly}
                  onChange={(e) => setFormData({ ...formData, subscription_price_monthly: parseFloat(e.target.value) || 0 })}
                />
                {formData.subscription_price_monthly > 0 && (
                  <p className="text-xs text-slate-500">
                    With VAT: <span className="font-medium">£{calculateWithVAT(formData.subscription_price_monthly)}/mo</span>
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Yearly Price (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.subscription_price_yearly}
                  onChange={(e) => setFormData({ ...formData, subscription_price_yearly: parseFloat(e.target.value) || 0 })}
                />
                {formData.subscription_price_yearly > 0 && (
                  <p className="text-xs text-slate-500">
                    With VAT: <span className="font-medium">£{calculateWithVAT(formData.subscription_price_yearly)}/yr</span>
                  </p>
                )}
              </div>
            </div>
          )}

          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-[#0D1B2A] hover:bg-[#1B3A4B]"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Pricing
          </Button>
        </div>
      </div>
    </div>
  );
};

// Transaction Detail Modal
const TransactionDetailModal = ({ id, onClose, onRefunded, onChanged }) => {
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(true);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('requested_by_customer');
  const [refunding, setRefunding] = useState(false);
  const [refundDone, setRefundDone] = useState(false);
  const [fakeBusy, setFakeBusy] = useState('');

  const loadDetail = () => {
    setDetailLoading(true);
    return paymentsAPI
      .getPaymentDetail(id)
      .then((res) => setDetail(res.data))
      .catch(() => toast.error('Failed to load transaction details'))
      .finally(() => setDetailLoading(false));
  };

  useEffect(() => {
    loadDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // kind: 'transaction' | 'email' | 'user'; flagged: current state
  const toggleFake = async (kind, value, flagged) => {
    if (!value) return;
    setFakeBusy(kind);
    try {
      if (flagged) await paymentsAPI.unmarkFake(kind, value);
      else await paymentsAPI.markFake(kind, value);
      await loadDetail();
      onChanged?.();
    } catch {
      toast.error('Failed to update test flag');
    } finally {
      setFakeBusy('');
    }
  };

  const handleRefund = async () => {
    const trimmed = refundAmount.trim();
    const parsedAmount = trimmed === '' ? undefined : parseFloat(trimmed);
    if (parsedAmount !== undefined && (isNaN(parsedAmount) || parsedAmount <= 0)) {
      toast.error('Enter a valid positive amount, or leave blank for a full refund');
      return;
    }
    const confirmed = window.confirm(
      parsedAmount !== undefined
        ? `Issue a partial refund of £${parsedAmount.toFixed(2)}? This cannot be undone.`
        : 'Issue a FULL refund? This cannot be undone.'
    );
    if (!confirmed) return;

    setRefunding(true);
    try {
      const body = { reason: refundReason };
      if (parsedAmount !== undefined) body.amount = parsedAmount;
      await paymentsAPI.refundPayment(id, body);
      toast.success('Refund initiated — access updates after Stripe confirms');
      setRefundDone(true);
      onRefunded();
    } catch (err) {
      toast.error('Failed to issue refund');
    } finally {
      setRefunding(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-100 sticky top-0 bg-white">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-[#0D1B2A]">Transaction Detail</h2>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <X className="w-5 h-5" />
            </button>
          </div>
          {detail && (
            <p className="text-slate-500 mt-1 text-sm font-mono truncate">{detail.id}</p>
          )}
        </div>

        <div className="p-6 space-y-6">
          {detailLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-[#41EAD4] animate-spin" />
            </div>
          ) : !detail ? (
            <p className="text-center text-slate-500 py-8">Failed to load details.</p>
          ) : (
            <>
              {/* Customer */}
              <div>
                <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-2">Customer</h3>
                <div className="bg-slate-50 rounded-xl p-4 space-y-1">
                  <p className="font-medium text-[#0D1B2A]">{detail.customer?.name}</p>
                  <p className="text-sm text-slate-500">{detail.customer?.email}</p>
                  {detail.customer?.stripe_customer_id && (
                    <p className="text-xs text-slate-400 font-mono">{detail.customer.stripe_customer_id}</p>
                  )}
                </div>
              </div>

              {/* Item */}
              <div>
                <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-2">Item</h3>
                <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                  <p className="font-medium text-[#0D1B2A]">{detail.item?.title}</p>
                  <div className="flex gap-2 items-center flex-wrap">
                    {detail.item?.type && (
                      <Badge variant="outline" className="capitalize">{detail.item.type}</Badge>
                    )}
                    {detail.domain && (
                      <Badge variant="outline" className="capitalize">{detail.domain}</Badge>
                    )}
                    <span className="text-xs text-slate-400 font-mono">{getStatusBadge(detail.payment_status)}</span>
                  </div>
                </div>
              </div>

              {/* Breakdown */}
              <div>
                <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-2">Breakdown</h3>
                <div className="bg-slate-50 rounded-xl p-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Base</span>
                    <span className="font-medium">£{(detail.breakdown?.base ?? 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">VAT</span>
                    <span className="font-medium">£{(detail.breakdown?.vat ?? 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between border-t border-slate-200 pt-2 font-semibold">
                    <span className="text-[#0D1B2A]">Total</span>
                    <span className="text-[#0D1B2A]">£{(detail.breakdown?.total ?? 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Stripe Deep Links */}
              {(detail.stripe?.payment_url || detail.stripe?.customer_url || detail.stripe?.subscription_url) && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-2">Stripe Links</h3>
                  <div className="flex flex-wrap gap-2">
                    {detail.stripe.payment_url && (
                      <a
                        href={detail.stripe.payment_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-[#0D1B2A] transition-colors"
                      >
                        <ExternalLink className="w-3 h-3" /> Payment Intent
                      </a>
                    )}
                    {detail.stripe.customer_url && (
                      <a
                        href={detail.stripe.customer_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-[#0D1B2A] transition-colors"
                      >
                        <ExternalLink className="w-3 h-3" /> Customer
                      </a>
                    )}
                    {detail.stripe.subscription_url && (
                      <a
                        href={detail.stripe.subscription_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-[#0D1B2A] transition-colors"
                      >
                        <ExternalLink className="w-3 h-3" /> Subscription
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Status Timeline */}
              {detail.timeline && detail.timeline.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-2">Status Timeline</h3>
                  <div className="space-y-2">
                    {detail.timeline.map((event, idx) => (
                      <div key={idx} className="flex items-start gap-3 text-sm">
                        <div className="w-2 h-2 rounded-full bg-[#41EAD4] mt-1.5 shrink-0" />
                        <div>
                          <span className="font-medium text-[#0D1B2A] capitalize">{event.state}</span>
                          {event.amount != null && (
                            <span className="text-slate-500 ml-2">£{Number(event.amount).toFixed(2)}</span>
                          )}
                          <p className="text-xs text-slate-400">
                            {new Date(event.at).toLocaleString('en-GB')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Refunds List */}
              {detail.refunds && detail.refunds.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-2">Refunds</h3>
                  <div className="space-y-2">
                    {detail.refunds.map((refund, idx) => (
                      <div key={refund.id ?? idx} className="flex justify-between items-center text-sm bg-red-50 rounded-lg p-3">
                        <div>
                          <p className="font-medium text-red-700">
                            £{Number(refund.amount).toFixed(2)}
                          </p>
                          <p className="text-xs text-slate-500">
                            {new Date(refund.created_at).toLocaleString('en-GB')}
                          </p>
                        </div>
                        <Badge className="bg-red-100 text-red-700">Refunded</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Webhook Events */}
              {detail.webhook_events && detail.webhook_events.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-2">Webhook Events</h3>
                  <div className="space-y-1">
                    {detail.webhook_events.map((evt, idx) => (
                      <div key={evt.event_id ?? idx} className="flex items-center justify-between text-xs bg-slate-50 rounded-lg p-2 gap-2">
                        <span className="font-mono text-slate-600 truncate">{evt.type}</span>
                        <span className="text-slate-400 font-mono truncate">{evt.event_id}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Test / Fake marking */}
              <div className="border border-slate-200 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-[#0D1B2A] flex items-center gap-1.5">
                    <FlaskConical className="w-4 h-4 text-amber-600" /> Test / Fake
                  </h3>
                  {detail.is_fake && (
                    <Badge className="bg-amber-100 text-amber-700">Excluded from totals</Badge>
                  )}
                </div>
                <p className="text-xs text-slate-500">
                  Flagged items are hidden from the list and left out of revenue totals.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline" size="sm"
                    disabled={fakeBusy === 'transaction'}
                    onClick={() => toggleFake('transaction', detail.id, detail.fake?.transaction)}
                    className={detail.fake?.transaction ? 'border-amber-400 text-amber-700 bg-amber-50' : ''}
                  >
                    {fakeBusy === 'transaction' && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                    {detail.fake?.transaction ? 'Unmark this transaction' : 'Mark this transaction'}
                  </Button>

                  {detail.customer?.email && detail.customer.email !== 'Unknown' && (
                    <Button
                      variant="outline" size="sm"
                      disabled={fakeBusy === 'email'}
                      onClick={() => toggleFake('email', detail.customer.email, detail.fake?.email)}
                      className={detail.fake?.email ? 'border-amber-400 text-amber-700 bg-amber-50' : ''}
                    >
                      {fakeBusy === 'email' && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                      {detail.fake?.email ? 'Unflag email' : 'Flag all from this email'}
                    </Button>
                  )}

                  {detail.customer?.user_id && (
                    <Button
                      variant="outline" size="sm"
                      disabled={fakeBusy === 'user'}
                      onClick={() => toggleFake('user', detail.customer.user_id, detail.fake?.user)}
                      className={detail.fake?.user ? 'border-amber-400 text-amber-700 bg-amber-50' : ''}
                    >
                      {fakeBusy === 'user' && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                      {detail.fake?.user ? 'Unflag user account' : 'Flag user account'}
                    </Button>
                  )}
                </div>
              </div>

              {/* Refund Control */}
              <div className="border border-slate-200 rounded-xl p-4 space-y-4">
                <h3 className="text-sm font-semibold text-[#0D1B2A]">Issue Refund</h3>
                <div className="flex gap-3 flex-wrap">
                  <div className="flex-1 min-w-[140px]">
                    <label className="text-xs text-slate-500 mb-1 block">Amount (£) — blank for full refund</label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="Leave blank for full"
                      value={refundAmount}
                      onChange={(e) => setRefundAmount(e.target.value)}
                    />
                  </div>
                  <div className="flex-1 min-w-[160px]">
                    <label className="text-xs text-slate-500 mb-1 block">Reason</label>
                    <select
                      value={refundReason}
                      onChange={(e) => setRefundReason(e.target.value)}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0D1B2A] focus:outline-none focus:ring-2 focus:ring-[#41EAD4]"
                    >
                      <option value="requested_by_customer">Requested by customer</option>
                      <option value="duplicate">Duplicate</option>
                      <option value="fraudulent">Fraudulent</option>
                    </select>
                  </div>
                </div>
                {refundDone && (
                  <p className="text-xs text-blue-600">
                    Refund initiated — access updates after Stripe confirms (webhook-reconciled).
                  </p>
                )}
                <Button
                  onClick={handleRefund}
                  disabled={refunding}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {refunding ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <AlertCircle className="w-4 h-4 mr-2" />
                  )}
                  Issue Refund
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const PAGE_SIZE = 25;

const TABS = [
  { key: 'transactions', label: 'Transactions' },
  { key: 'subscriptions', label: 'Subscriptions' },
  { key: 'reconciliation', label: 'Reconciliation' },
  { key: 'pricing', label: 'Course Pricing' },
  { key: 'vat', label: 'VAT Report' },
  { key: 'analytics', label: 'Analytics' },
];

export const AdminPaymentsPage = () => {
  // Page-level loading (for pricing/VAT data)
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('transactions');

  // Pricing / VAT state
  const [courses, setCourses] = useState([]);
  const [vatReport, setVatReport] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);

  // Transactions state
  const [filters, setFilters] = useState({
    status: '',
    domain: '',
    search: '',
    date_from: '',
    date_to: '',
  });
  const [page, setPage] = useState(1);
  const [showFake, setShowFake] = useState(false); // hide test transactions by default
  const [txData, setTxData] = useState({ items: [], total: 0, summary: {} });
  const [txRefresh, setTxRefresh] = useState(0);
  const [txLoading, setTxLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [arrears, setArrears] = useState({ items: [], total: 0 });

  // Subscriptions state
  const [subFilters, setSubFilters] = useState({ status: '', domain: '' });
  const [subItems, setSubItems] = useState([]);
  const [subLoading, setSubLoading] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);

  // Reconciliation state
  const [reconData, setReconData] = useState(null);
  const [reconLoading, setReconLoading] = useState(false);

  // Analytics state
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsFilters, setAnalyticsFilters] = useState({ date_from: '', date_to: '' });

  // Initial fetch: courses + VAT report (for Pricing/VAT tabs)
  const fetchData = async () => {
    try {
      const [coursesRes, vatRes] = await Promise.all([
        coursesAPI.getAll(),
        paymentsAPI.getVATReport(),
      ]);
      setCourses(coursesRes.data);
      setVatReport(vatRes.data);
    } catch (err) {
      toast.error('Failed to load payment data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Fetch transactions whenever the tab is active or filters/page change
  useEffect(() => {
    if (activeTab !== 'transactions') return;
    setTxLoading(true);
    const params = Object.fromEntries(
      Object.entries({ ...filters, page, page_size: PAGE_SIZE }).filter(([, v]) => v !== '')
    );
    if (showFake) params.include_fake = true;
    paymentsAPI
      .listPayments(params)
      .then((res) => setTxData(res.data))
      .catch(() => toast.error('Failed to load transactions'))
      .finally(() => setTxLoading(false));
  }, [activeTab, filters, page, showFake, txRefresh]);

  // Arrears (manual monthly plans with overdue rows) — refreshes with the tab
  useEffect(() => {
    if (activeTab !== 'transactions') return;
    paymentsAPI
      .getArrears()
      .then((res) => setArrears(res.data))
      .catch(() => {});  // non-blocking: the card just stays hidden
  }, [activeTab, txRefresh]);

  // Delete a transaction row (test/junk records). Paid rows must be marked
  // as test first — the backend refuses otherwise.
  const handleDeleteTx = (e, row) => {
    e.stopPropagation();
    const who = row.user_email || row.user_name || 'unknown customer';
    if (!window.confirm(`Delete this ${row.domain} transaction for ${who}?\n\nThis permanently removes the record from the database.`)) return;
    paymentsAPI
      .deletePayment(row.id)
      .then(() => {
        toast.success('Transaction deleted');
        setTxRefresh((n) => n + 1);
      })
      .catch((err) => toast.error(err.response?.data?.detail || 'Failed to delete transaction'));
  };

  // Fetch subscriptions whenever the tab is active or sub-filters change
  const fetchSubscriptions = () => {
    setSubLoading(true);
    const params = Object.fromEntries(
      Object.entries(subFilters).filter(([, v]) => v !== '')
    );
    paymentsAPI
      .listSubscriptions(params)
      .then((res) => setSubItems(res.data?.items ?? []))
      .catch(() => toast.error('Failed to load subscriptions'))
      .finally(() => setSubLoading(false));
  };

  useEffect(() => {
    if (activeTab !== 'subscriptions') return;
    fetchSubscriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, subFilters]);

  // Fetch reconciliation data when tab is active
  useEffect(() => {
    if (activeTab !== 'reconciliation') return;
    setReconLoading(true);
    paymentsAPI
      .getReconciliation()
      .then((res) => setReconData(res.data))
      .catch(() => toast.error('Failed to load reconciliation data'))
      .finally(() => setReconLoading(false));
  }, [activeTab]);

  // Fetch analytics data when tab is active or date filters change
  useEffect(() => {
    if (activeTab !== 'analytics') return;
    setAnalyticsLoading(true);
    const params = Object.fromEntries(
      Object.entries(analyticsFilters).filter(([, v]) => v !== '')
    );
    paymentsAPI
      .getAnalytics(params)
      .then((res) => setAnalyticsData(res.data))
      .catch(() => toast.error('Failed to load analytics data'))
      .finally(() => setAnalyticsLoading(false));
  }, [activeTab, analyticsFilters]);

  const handleCancelSubscription = async (row) => {
    const confirmed = window.confirm(
      `Cancel subscription for ${row.user_name} (${row.item_title})? This cannot be undone.`
    );
    if (!confirmed) return;
    setCancellingId(row.stripe_subscription_id);
    try {
      await paymentsAPI.cancelSubscription(row.stripe_subscription_id);
      toast.success('Cancellation initiated — reconciled by Stripe');
      fetchSubscriptions();
    } catch {
      toast.error('Failed to cancel subscription');
    } finally {
      setCancellingId(null);
    }
  };

  const getSubStatusBadge = (status) => {
    if (status === 'active')
      return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle className="w-3 h-3 mr-1" />Active</Badge>;
    if (status === 'past_due')
      return <Badge className="bg-yellow-100 text-yellow-700"><Clock className="w-3 h-3 mr-1" />Past Due</Badge>;
    if (status === 'canceled' || status === 'cancelled')
      return <Badge className="bg-red-100 text-red-700"><AlertCircle className="w-3 h-3 mr-1" />Cancelled</Badge>;
    if (status === 'trialing')
      return <Badge className="bg-blue-100 text-blue-700"><Clock className="w-3 h-3 mr-1" />Trialing</Badge>;
    return <Badge className="bg-slate-100 text-slate-700">{status}</Badge>;
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const resetFilters = () => {
    setFilters({ status: '', domain: '', search: '', date_from: '', date_to: '' });
    setShowFake(false);
    setPage(1);
  };

  // Re-fetch the transactions list without closing the detail modal
  const refreshTransactions = () => setFilters((f) => ({ ...f }));

  const handleExportCsv = async () => {
    try {
      const currentFilters = Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v !== '')
      );
      if (showFake) currentFilters.include_fake = true;
      const res = await paymentsAPI.exportTransactionsCsv(currentFilters);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'transactions.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to export CSV');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-[#41EAD4] animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-payments-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-[#0D1B2A] mb-2">Payment Management</h1>
          <p className="text-slate-500">Manage course pricing, VAT, and view transactions</p>
        </div>

        {/* Tab Bar */}
        <div className="flex gap-2 border-b border-slate-200 overflow-x-auto">
          {TABS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
                activeTab === key
                  ? 'text-[#0D1B2A] border-b-2 border-[#41EAD4]'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* ── Transactions Tab ── */}
        {activeTab === 'transactions' && (
          <div className="space-y-4">
            {/* Summary Cards */}
            {txData.summary && Object.keys(txData.summary).length > 0 && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="border-slate-100">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Total Revenue</p>
                        <p className="text-xl font-bold text-[#0D1B2A]">
                          £{(txData.summary.total_revenue ?? 0).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-slate-100">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                        <Receipt className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">VAT Collected</p>
                        <p className="text-xl font-bold text-[#0D1B2A]">
                          £{(txData.summary.total_vat_collected ?? 0).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-slate-100">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Paid</p>
                        <p className="text-xl font-bold text-[#0D1B2A]">
                          {txData.summary.paid_transactions ?? 0}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-slate-100">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center">
                        <Clock className="w-5 h-5 text-yellow-600" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Pending</p>
                        <p className="text-xl font-bold text-[#0D1B2A]">
                          {txData.summary.pending_transactions ?? 0}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Arrears — overdue manual monthly plan rows (spec §4) */}
            {arrears.total > 0 && (
              <Card className="border-red-200">
                <CardHeader className="pb-0">
                  <CardTitle className="text-lg flex items-center gap-2 text-red-700">
                    <AlertCircle className="w-5 h-5" />
                    Arrears
                    <span className="text-sm font-normal text-slate-500">
                      ({arrears.total} booking{arrears.total === 1 ? '' : 's'} overdue)
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="text-left text-xs text-slate-500 border-b">
                          <th className="pb-3 font-medium">Customer</th>
                          <th className="pb-3 font-medium">Programme</th>
                          <th className="pb-3 font-medium text-right">Overdue rows</th>
                          <th className="pb-3 font-medium text-right">Overdue amount</th>
                          <th className="pb-3 font-medium">Oldest due</th>
                        </tr>
                      </thead>
                      <tbody className="text-sm">
                        {arrears.items.map((row) => (
                          <tr key={row.booking_id} className="border-b border-slate-50">
                            <td className="py-3">
                              <p className="font-medium text-[#0D1B2A]">{row.customer_name || 'Unknown'}</p>
                              <p className="text-xs text-slate-500">{row.customer_email}</p>
                            </td>
                            <td className="py-3 text-slate-600">{row.programme_title || row.programme_slug}</td>
                            <td className="py-3 text-right font-medium text-red-600">{row.overdue_count}</td>
                            <td className="py-3 text-right font-medium text-red-600">
                              £{(row.overdue_amount || 0).toFixed(2)}
                            </td>
                            <td className="py-3 text-slate-600">
                              {new Date(row.oldest_due_date).toLocaleDateString('en-GB')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Filter Bar */}
            <Card className="border-slate-100">
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-3 items-end">
                  <div className="flex-1 min-w-[160px]">
                    <label className="text-xs text-slate-500 mb-1 block">Status</label>
                    <select
                      value={filters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0D1B2A] focus:outline-none focus:ring-2 focus:ring-[#41EAD4]"
                    >
                      <option value="">All Statuses</option>
                      <option value="paid">Paid</option>
                      <option value="pending">Pending</option>
                      <option value="refunded">Refunded</option>
                      <option value="partially_refunded">Partially Refunded</option>
                    </select>
                  </div>

                  <div className="flex-1 min-w-[140px]">
                    <label className="text-xs text-slate-500 mb-1 block">Type</label>
                    <select
                      value={filters.domain}
                      onChange={(e) => handleFilterChange('domain', e.target.value)}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0D1B2A] focus:outline-none focus:ring-2 focus:ring-[#41EAD4]"
                    >
                      <option value="">All Types</option>
                      <option value="course">Course</option>
                      <option value="programme">Programme</option>
                    </select>
                  </div>

                  <div className="flex-1 min-w-[180px]">
                    <label className="text-xs text-slate-500 mb-1 block">Search</label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Name or email…"
                        value={filters.search}
                        onChange={(e) => handleFilterChange('search', e.target.value)}
                        className="pl-9"
                      />
                    </div>
                  </div>

                  <div className="flex-1 min-w-[140px]">
                    <label className="text-xs text-slate-500 mb-1 block">From</label>
                    <Input
                      type="date"
                      value={filters.date_from}
                      onChange={(e) => handleFilterChange('date_from', e.target.value)}
                    />
                  </div>

                  <div className="flex-1 min-w-[140px]">
                    <label className="text-xs text-slate-500 mb-1 block">To</label>
                    <Input
                      type="date"
                      value={filters.date_to}
                      onChange={(e) => handleFilterChange('date_to', e.target.value)}
                    />
                  </div>

                  <Button
                    variant="outline"
                    onClick={() => { setShowFake((v) => !v); setPage(1); }}
                    className={`shrink-0 ${showFake ? 'border-[#41EAD4] text-[#0D1B2A] bg-[#41EAD4]/10' : ''}`}
                    title="Test transactions are hidden and excluded from totals by default"
                  >
                    {showFake ? <Eye className="w-4 h-4 mr-1" /> : <EyeOff className="w-4 h-4 mr-1" />}
                    {showFake ? 'Showing test' : 'Show test'}
                  </Button>

                  <Button variant="outline" onClick={resetFilters} className="shrink-0">
                    <RefreshCw className="w-4 h-4 mr-1" /> Reset
                  </Button>

                  <Button variant="outline" onClick={handleExportCsv} className="shrink-0">
                    <Download className="w-4 h-4 mr-1" /> Export CSV
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Transactions Table */}
            <Card className="border-slate-100">
              <CardHeader className="pb-0">
                <CardTitle className="text-lg">
                  Transactions
                  {txData.total > 0 && (
                    <span className="text-sm font-normal text-slate-500 ml-2">
                      ({txData.total} total)
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                {txLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-[#41EAD4] animate-spin" />
                  </div>
                ) : txData.items.length === 0 ? (
                  <div className="text-center py-10 text-slate-500">
                    <CreditCard className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                    <p>No transactions found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="text-left text-xs text-slate-500 border-b">
                          <th className="pb-3 font-medium">Date</th>
                          <th className="pb-3 font-medium">Customer</th>
                          <th className="pb-3 font-medium">Item</th>
                          <th className="pb-3 font-medium">Type</th>
                          <th className="pb-3 font-medium text-right">Amount</th>
                          <th className="pb-3 font-medium">Status</th>
                          <th className="pb-3 font-medium text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="text-sm">
                        {txData.items.map((row) => (
                          <tr
                            key={row.id}
                            className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer transition-colors"
                            onClick={() => setSelectedId(row.id)}
                          >
                            <td className="py-3 text-slate-600">
                              {new Date(row.created_at).toLocaleDateString('en-GB')}
                            </td>
                            <td className="py-3">
                              <p className="font-medium text-[#0D1B2A] flex items-center gap-1.5">
                                {row.user_name}
                                {row.is_fake && (
                                  <Badge className="bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0">
                                    <FlaskConical className="w-3 h-3 mr-0.5" />Test
                                  </Badge>
                                )}
                              </p>
                              <p className="text-xs text-slate-500">{row.user_email}</p>
                            </td>
                            <td className="py-3 text-slate-600">{row.item_title}</td>
                            <td className="py-3">
                              <Badge variant="outline" className="capitalize">
                                {row.domain}
                              </Badge>
                            </td>
                            <td className="py-3 text-right font-medium text-[#0D1B2A]">
                              £{(row.amount_total || 0).toFixed(2)}
                            </td>
                            <td className="py-3">{getStatusBadge(row.payment_status)}</td>
                            <td className="py-3 text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => handleDeleteTx(e, row)}
                                className="text-slate-400 hover:text-red-600 hover:bg-red-50"
                                title="Delete this transaction record"
                                data-testid={`delete-tx-${row.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Pagination */}
                {txData.total > 0 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
                    <span className="text-sm text-slate-500">
                      Page {page} · {txData.total} total
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page === 1}
                        onClick={() => setPage((p) => p - 1)}
                      >
                        <ChevronLeft className="w-4 h-4 mr-1" /> Prev
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page * PAGE_SIZE >= txData.total}
                        onClick={() => setPage((p) => p + 1)}
                      >
                        Next <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

          </div>
        )}

        {/* ── Subscriptions & Installments Tab ── */}
        {activeTab === 'subscriptions' && (
          <div className="space-y-4">
            {/* Filter Bar */}
            <Card className="border-slate-100">
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-3 items-end">
                  <div className="flex-1 min-w-[160px]">
                    <label className="text-xs text-slate-500 mb-1 block">Status</label>
                    <select
                      value={subFilters.status}
                      onChange={(e) => setSubFilters((f) => ({ ...f, status: e.target.value }))}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0D1B2A] focus:outline-none focus:ring-2 focus:ring-[#41EAD4]"
                    >
                      <option value="">All Statuses</option>
                      <option value="active">Active</option>
                      <option value="past_due">Past Due</option>
                      <option value="canceled">Cancelled</option>
                      <option value="trialing">Trialing</option>
                    </select>
                  </div>

                  <div className="flex-1 min-w-[140px]">
                    <label className="text-xs text-slate-500 mb-1 block">Type</label>
                    <select
                      value={subFilters.domain}
                      onChange={(e) => setSubFilters((f) => ({ ...f, domain: e.target.value }))}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0D1B2A] focus:outline-none focus:ring-2 focus:ring-[#41EAD4]"
                    >
                      <option value="">All Types</option>
                      <option value="course">Course</option>
                      <option value="programme">Programme</option>
                    </select>
                  </div>

                  <Button
                    variant="outline"
                    onClick={() => setSubFilters({ status: '', domain: '' })}
                    className="shrink-0"
                  >
                    <RefreshCw className="w-4 h-4 mr-1" /> Reset
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Subscriptions Table */}
            <Card className="border-slate-100">
              <CardHeader className="pb-0">
                <CardTitle className="text-lg">Subscriptions &amp; Installments</CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                {subLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-[#41EAD4] animate-spin" />
                  </div>
                ) : subItems.length === 0 ? (
                  <div className="text-center py-10 text-slate-500">
                    <CreditCard className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                    <p>No subscriptions</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="text-left text-xs text-slate-500 border-b">
                          <th className="pb-3 font-medium">Customer</th>
                          <th className="pb-3 font-medium">Item</th>
                          <th className="pb-3 font-medium">Type</th>
                          <th className="pb-3 font-medium">Kind</th>
                          <th className="pb-3 font-medium">Status</th>
                          <th className="pb-3 font-medium">Next Billing</th>
                          <th className="pb-3 font-medium">Progress</th>
                          <th className="pb-3 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="text-sm">
                        {subItems.map((row) => {
                          const progress =
                            row.payments_made != null
                              ? `${row.payments_made} / ${row.payments_made + (row.payments_remaining ?? 0)}`
                              : '—';
                          const nextBilling = row.current_period_end
                            ? new Date(row.current_period_end).toLocaleDateString('en-GB')
                            : '—';
                          const isCancelling = cancellingId === row.stripe_subscription_id;
                          return (
                            <tr
                              key={row.id ?? row.stripe_subscription_id}
                              className="border-b border-slate-50 hover:bg-slate-50 transition-colors"
                            >
                              <td className="py-3">
                                <p className="font-medium text-[#0D1B2A]">{row.user_name}</p>
                                <p className="text-xs text-slate-500">{row.user_email}</p>
                              </td>
                              <td className="py-3 text-slate-600">{row.item_title}</td>
                              <td className="py-3">
                                <Badge variant="outline" className="capitalize">{row.domain}</Badge>
                              </td>
                              <td className="py-3 text-slate-600 capitalize">{row.kind}</td>
                              <td className="py-3">{getSubStatusBadge(row.status)}</td>
                              <td className="py-3 text-slate-600">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3 text-slate-400" />
                                  {nextBilling}
                                </span>
                              </td>
                              <td className="py-3 text-slate-600">{progress}</td>
                              <td className="py-3">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={isCancelling || row.status === 'canceled' || row.status === 'cancelled'}
                                  onClick={() => handleCancelSubscription(row)}
                                  className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                                >
                                  {isCancelling ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    'Cancel'
                                  )}
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* ── Reconciliation Tab ── */}
        {activeTab === 'reconciliation' && (
          <div className="space-y-6">
            {reconLoading ? (
              <div className="flex items-center justify-center py-24">
                <Loader2 className="w-8 h-8 text-[#41EAD4] animate-spin" />
              </div>
            ) : (
              <>
                {/* Section 1: Paid but unenrolled */}
                <Card className="border-slate-100">
                  <CardHeader className="pb-0">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-orange-500" />
                      Paid but Unenrolled
                      <span className="text-sm font-normal text-slate-500 ml-1">
                        ({(reconData?.paid_but_unenrolled ?? []).length})
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    {(reconData?.paid_but_unenrolled ?? []).length === 0 ? (
                      <div className="text-center py-6 text-slate-400">
                        <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
                        <p className="font-medium">All clear ✓</p>
                        <p className="text-sm">No paid-but-unenrolled records found</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="text-left text-xs text-slate-500 border-b">
                              <th className="pb-3 font-medium">ID</th>
                              <th className="pb-3 font-medium">Domain</th>
                              <th className="pb-3 font-medium">User ID</th>
                              <th className="pb-3 font-medium">Course / Programme</th>
                            </tr>
                          </thead>
                          <tbody className="text-sm">
                            {(reconData?.paid_but_unenrolled ?? []).map((row) => (
                              <tr
                                key={row.id}
                                className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer transition-colors"
                                onClick={() => setSelectedId(row.id)}
                              >
                                <td className="py-3 font-mono text-xs text-slate-600 truncate max-w-[180px]">{row.id}</td>
                                <td className="py-3">
                                  <Badge variant="outline" className="capitalize">{row.domain}</Badge>
                                </td>
                                <td className="py-3 font-mono text-xs text-slate-500 truncate max-w-[140px]">{row.user_id}</td>
                                <td className="py-3 text-slate-600">
                                  {row.course_id ?? row.programme_slug ?? '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Section 2: Failed webhook events */}
                <Card className="border-slate-100">
                  <CardHeader className="pb-0">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-red-500" />
                      Failed Webhook Events
                      <span className="text-sm font-normal text-slate-500 ml-1">
                        ({(reconData?.webhook_failed ?? []).length})
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    {(reconData?.webhook_failed ?? []).length === 0 ? (
                      <div className="text-center py-6 text-slate-400">
                        <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
                        <p className="font-medium">All clear ✓</p>
                        <p className="text-sm">No failed webhook events</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="text-left text-xs text-slate-500 border-b">
                              <th className="pb-3 font-medium">Event ID</th>
                              <th className="pb-3 font-medium">Type</th>
                              <th className="pb-3 font-medium">Error</th>
                              <th className="pb-3 font-medium">Received At</th>
                            </tr>
                          </thead>
                          <tbody className="text-sm">
                            {(reconData?.webhook_failed ?? []).map((evt) => (
                              <tr key={evt.event_id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                                <td className="py-3 font-mono text-xs text-slate-600 truncate max-w-[180px]">{evt.event_id}</td>
                                <td className="py-3 text-slate-600">{evt.type}</td>
                                <td className="py-3 text-red-600 text-xs max-w-[260px] truncate">{evt.error}</td>
                                <td className="py-3 text-slate-500 text-xs whitespace-nowrap">
                                  {evt.received_at ? new Date(evt.received_at).toLocaleString('en-GB') : '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Section 3: Amount mismatches */}
                <Card className="border-slate-100">
                  <CardHeader className="pb-0">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-yellow-500" />
                      Amount Mismatches
                      <span className="text-sm font-normal text-slate-500 ml-1">
                        ({(reconData?.amount_mismatch ?? []).length})
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    {(reconData?.amount_mismatch ?? []).length === 0 ? (
                      <div className="text-center py-6 text-slate-400">
                        <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
                        <p className="font-medium">No mismatches detected</p>
                        <p className="text-sm">Stored amounts match Stripe</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="text-left text-xs text-slate-500 border-b">
                              <th className="pb-3 font-medium">ID</th>
                              <th className="pb-3 font-medium text-right">Stored (£)</th>
                              <th className="pb-3 font-medium text-right">Stripe (£)</th>
                              <th className="pb-3 font-medium text-right">Difference</th>
                            </tr>
                          </thead>
                          <tbody className="text-sm">
                            {(reconData?.amount_mismatch ?? []).map((row) => {
                              const diff = (Number(row.stripe) - Number(row.stored)).toFixed(2);
                              return (
                                <tr key={row.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                                  <td className="py-3 font-mono text-xs text-slate-600 truncate max-w-[180px]">{row.id}</td>
                                  <td className="py-3 text-right text-slate-600">£{Number(row.stored).toFixed(2)}</td>
                                  <td className="py-3 text-right text-slate-600">£{Number(row.stripe).toFixed(2)}</td>
                                  <td className={`py-3 text-right font-medium ${Number(diff) !== 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                                    {Number(diff) >= 0 ? '+' : ''}£{diff}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        )}

        {/* ── Pricing Tab — PRESERVED ── */}
        {activeTab === 'pricing' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {courses.map((course) => (
              <Card key={course.course_id} className="border-slate-100">
                <CardContent className="p-4">
                  <h3 className="font-medium text-[#0D1B2A] mb-2">{course.title}</h3>

                  <div className="space-y-2 text-sm mb-4">
                    <div className="flex justify-between">
                      <span className="text-slate-500">One-Time Price</span>
                      <span className="font-medium">£{(course.price || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">VAT Rate</span>
                      <span className="font-medium">{course.vat_rate || 20}%</span>
                    </div>
                    {course.subscription_enabled && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Monthly</span>
                          <span className="font-medium">£{(course.subscription_price_monthly || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Yearly</span>
                          <span className="font-medium">£{(course.subscription_price_yearly || 0).toFixed(2)}</span>
                        </div>
                      </>
                    )}
                  </div>

                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => setSelectedCourse(course)}
                  >
                    Edit Pricing
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* ── VAT Report Tab — PRESERVED ── */}
        {activeTab === 'vat' && vatReport && (
          <Card className="border-slate-100">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#41EAD4]" />
                VAT Report by Month
              </CardTitle>
            </CardHeader>
            <CardContent>
              {Object.keys(vatReport.monthly_breakdown || {}).length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Receipt className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>No VAT data available yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-xs text-slate-500 border-b">
                        <th className="pb-3 font-medium">Month</th>
                        <th className="pb-3 font-medium text-right">Transactions</th>
                        <th className="pb-3 font-medium text-right">Net Sales</th>
                        <th className="pb-3 font-medium text-right">VAT Collected</th>
                        <th className="pb-3 font-medium text-right">Total Sales</th>
                      </tr>
                    </thead>
                    <tbody className="text-sm">
                      {Object.entries(vatReport.monthly_breakdown)
                        .sort((a, b) => b[0].localeCompare(a[0]))
                        .map(([month, data]) => (
                          <tr key={month} className="border-b border-slate-50">
                            <td className="py-3 font-medium text-[#0D1B2A]">
                              {new Date(month + '-01').toLocaleDateString('en-GB', {
                                month: 'long',
                                year: 'numeric',
                              })}
                            </td>
                            <td className="py-3 text-right text-slate-600">{data.transaction_count}</td>
                            <td className="py-3 text-right text-slate-600">£{data.net_sales.toFixed(2)}</td>
                            <td className="py-3 text-right text-blue-600 font-medium">
                              £{data.vat_collected.toFixed(2)}
                            </td>
                            <td className="py-3 text-right font-medium text-[#0D1B2A]">
                              £{data.total_sales.toFixed(2)}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* ── Analytics Tab ── */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            {/* Date Filter */}
            <Card className="border-slate-100">
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-3 items-end">
                  <div className="flex-1 min-w-[140px]">
                    <label className="text-xs text-slate-500 mb-1 block">From</label>
                    <Input
                      type="date"
                      value={analyticsFilters.date_from}
                      onChange={(e) =>
                        setAnalyticsFilters((f) => ({ ...f, date_from: e.target.value }))
                      }
                    />
                  </div>
                  <div className="flex-1 min-w-[140px]">
                    <label className="text-xs text-slate-500 mb-1 block">To</label>
                    <Input
                      type="date"
                      value={analyticsFilters.date_to}
                      onChange={(e) =>
                        setAnalyticsFilters((f) => ({ ...f, date_to: e.target.value }))
                      }
                    />
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setAnalyticsFilters({ date_from: '', date_to: '' })}
                    className="shrink-0"
                  >
                    <RefreshCw className="w-4 h-4 mr-1" /> Reset
                  </Button>
                </div>
              </CardContent>
            </Card>

            {analyticsLoading ? (
              <div className="flex items-center justify-center py-24">
                <Loader2 className="w-8 h-8 text-[#41EAD4] animate-spin" />
              </div>
            ) : !analyticsData ? (
              <div className="text-center py-16 text-slate-400">
                <BarChart2 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="font-medium">No analytics data available</p>
                <p className="text-sm">Try adjusting your date range</p>
              </div>
            ) : (
              <>
                {/* Summary Cards */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="border-slate-100">
                    <CardContent className="p-4">
                      <p className="text-xs text-slate-500 mb-1">MRR</p>
                      <p className="text-2xl font-bold text-[#0D1B2A]">
                        £{Number(analyticsData.mrr ?? 0).toFixed(2)}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">Monthly Recurring Revenue</p>
                    </CardContent>
                  </Card>

                  <Card className="border-slate-100">
                    <CardContent className="p-4">
                      <p className="text-xs text-slate-500 mb-1">ARR</p>
                      <p className="text-2xl font-bold text-[#0D1B2A]">
                        £{Number(analyticsData.arr ?? 0).toFixed(2)}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">Annual Recurring Revenue</p>
                    </CardContent>
                  </Card>

                  <Card className="border-slate-100">
                    <CardContent className="p-4">
                      <p className="text-xs text-slate-500 mb-1">Refund Rate</p>
                      <p className="text-2xl font-bold text-red-600">
                        {((analyticsData.refund_rate?.rate ?? 0) * 100).toFixed(1)}%
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        {analyticsData.refund_rate?.refunded_count ?? 0} of{' '}
                        {analyticsData.refund_rate?.paid_count ?? 0} transactions
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="border-slate-100">
                    <CardContent className="p-4">
                      <p className="text-xs text-slate-500 mb-1">Failed Payments</p>
                      <p className="text-2xl font-bold text-orange-600">
                        {analyticsData.failed_payment_count ?? 0}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">Requires attention</p>
                    </CardContent>
                  </Card>

                  <Card className="border-slate-100">
                    <CardContent className="p-4">
                      <p className="text-xs text-slate-500 mb-1">Total Revenue</p>
                      <p className="text-2xl font-bold text-[#0D1B2A]">
                        £{Number(analyticsData.totals?.total_revenue ?? 0).toFixed(2)}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        VAT: £{Number(analyticsData.totals?.total_vat ?? 0).toFixed(2)}
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="border-slate-100">
                    <CardContent className="p-4">
                      <p className="text-xs text-slate-500 mb-1">Net Revenue</p>
                      <p className="text-2xl font-bold text-emerald-600">
                        £{Number(analyticsData.totals?.net_revenue ?? 0).toFixed(2)}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        {analyticsData.totals?.transaction_count ?? 0} transactions
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="border-slate-100 sm:col-span-2">
                    <CardContent className="p-4">
                      <p className="text-xs text-slate-500 mb-1">New vs Renewal Revenue</p>
                      <div className="flex gap-6 mt-2">
                        <div>
                          <p className="text-xl font-bold text-[#41EAD4]">
                            £{Number(analyticsData.new_vs_renewal?.new_revenue ?? 0).toFixed(2)}
                          </p>
                          <p className="text-xs text-slate-400">New</p>
                        </div>
                        <div>
                          <p className="text-xl font-bold text-[#0D1B2A]">
                            £{Number(analyticsData.new_vs_renewal?.renewal_revenue ?? 0).toFixed(2)}
                          </p>
                          <p className="text-xs text-slate-400">Renewal</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Revenue Over Time */}
                {(analyticsData.revenue_over_time ?? []).length > 0 && (
                  <Card className="border-slate-100">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">Revenue Over Time</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={280}>
                        <AreaChart data={analyticsData.revenue_over_time}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#64748b' }} />
                          <YAxis
                            tick={{ fontSize: 12, fill: '#64748b' }}
                            tickFormatter={(v) => `£${Number(v).toFixed(0)}`}
                          />
                          <Tooltip
                            formatter={(value) => [`£${Number(value).toFixed(2)}`, 'Revenue']}
                          />
                          <Area
                            type="monotone"
                            dataKey="revenue"
                            stroke="#41EAD4"
                            fill="#41EAD4"
                            fillOpacity={0.15}
                            strokeWidth={2}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}

                {/* Revenue by Payment Type */}
                {(analyticsData.revenue_by_payment_type ?? []).length > 0 && (
                  <Card className="border-slate-100">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">Revenue by Payment Type</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={analyticsData.revenue_by_payment_type}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="payment_type" tick={{ fontSize: 12, fill: '#64748b' }} />
                          <YAxis
                            tick={{ fontSize: 12, fill: '#64748b' }}
                            tickFormatter={(v) => `£${Number(v).toFixed(0)}`}
                          />
                          <Tooltip
                            formatter={(value) => [`£${Number(value).toFixed(2)}`, 'Revenue']}
                          />
                          <Bar dataKey="revenue" fill="#0D1B2A" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}

                {/* Revenue by Item */}
                {(analyticsData.revenue_by_item ?? []).length > 0 && (
                  <Card className="border-slate-100">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">Revenue by Item</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={analyticsData.revenue_by_item} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis
                            type="number"
                            tick={{ fontSize: 11, fill: '#64748b' }}
                            tickFormatter={(v) => `£${Number(v).toFixed(0)}`}
                          />
                          <YAxis
                            type="category"
                            dataKey="title"
                            width={140}
                            tick={{ fontSize: 11, fill: '#64748b' }}
                          />
                          <Tooltip
                            formatter={(value) => [`£${Number(value).toFixed(2)}`, 'Revenue']}
                          />
                          <Bar dataKey="revenue" fill="#41EAD4" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}

                {/* Empty charts notice */}
                {(analyticsData.revenue_over_time ?? []).length === 0 &&
                  (analyticsData.revenue_by_payment_type ?? []).length === 0 &&
                  (analyticsData.revenue_by_item ?? []).length === 0 && (
                  <div className="text-center py-10 text-slate-400">
                    <BarChart2 className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                    <p>No chart data for this period</p>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Pricing Editor Modal — PRESERVED */}
        {selectedCourse && (
          <PricingEditorModal
            course={selectedCourse}
            onClose={() => setSelectedCourse(null)}
            onSave={fetchData}
          />
        )}

        {/* Transaction Detail Modal — lifted to top level so it works from any tab */}
        {selectedId && (
          <TransactionDetailModal
            id={selectedId}
            onClose={() => setSelectedId(null)}
            onRefunded={() => {
              setSelectedId(null);
              setFilters((f) => ({ ...f }));
            }}
            onChanged={refreshTransactions}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminPaymentsPage;
